import React, { useContext, useEffect, useState } from "react";
import {
  TouchableOpacity,
  StyleSheet,
  useColorScheme,
  Dimensions,
  Platform,
  Modal,
  View as RNView,
  FlatList,
} from "react-native";
import { useForm, Controller } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import { Picker } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import { TextInput } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import DeviceOrientationContext from "../../../../components/context/DeviceOrientationContext";
import { getOrganizations } from "../../../../redux/dispatcher/adminDispatcher";
import { createOrganizationalUser } from "../../../../services/adminService";
import { Text, View } from "../../../../components/shared/Themed";
import { AntDesign } from "@expo/vector-icons";

type Props = {
  navigation: any;
};

// Form data type definition
type FormData = {
  email: string;
  zone: string;
  firstName: string;
  lastName: string;
  login: string;
  team: string;
  password: string;
  // We'll handle company-role pairs separately
};

const { width, height } = Dimensions.get("window");

// Available time zones
const TIMEZONES = [
  { label: "Australia/Brisbane", value: "Australia/Brisbane" },
  { label: "Australia/Melbourne", value: "Australia/Melbourne" },
  { label: "Australia/Sydney", value: "Australia/Sydney" },
  { label: "Australia/Tasmania", value: "Australia/Tasmania" },
  { label: "Australia/Perth", value: "Australia/Perth" },
];

// Team options
const TEAMS = [
  { label: "Operation", value: "operation" },
  { label: "Sales", value: "sales" },
  { label: "Admin", value: "admin" },
  { label: "Warehouse", value: "warehouse" },
];

// Role options
const ROLES = [
  { label: "MEMBER", value: "MEMBER" },
  { label: "OWNER", value: "OWNER" },
  { label: "ADMIN", value: "ADMIN" },
];

export default function CreateAdminOrganizationUser({ navigation }: Props) {
  const colorScheme = useColorScheme();
  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const [loading, setLoading] = useState(false);
  const { loadingOrganizations, organizations } = useSelector(
    (state: any) => state.admin
  );
  const dispatch = useDispatch<any>();
  const toast = useToast();

  // State for company-role pairings
  const [companyRolePairs, setCompanyRolePairs] = useState<
    Array<{ companyId: string; role: string }>
  >([]);
  const [editPairModalVisible, setEditPairModalVisible] = useState(false);
  const [currentCompanyId, setCurrentCompanyId] = useState<string>("");
  const [currentRole, setCurrentRole] = useState<string>("");
  const [editingIndex, setEditingIndex] = useState<number>(-1);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      email: "",
      zone: "Australia/Sydney",
      firstName: "",
      lastName: "",
      login: "",
      team: "",
      password: "",
    },
  });

  // Fetch company list on component mount
  useEffect(() => {
    const fetchCompanyList = async () => {
      setLoading(true);
      dispatch(getOrganizations())
        .unwrap()
        .then(() => setLoading(false))
        .catch(() => setLoading(false));
    };
    fetchCompanyList();
  }, []);

  // Get company name by ID
  const getCompanyNameById = (id: string) => {
    const company = organizations.find((c: any) => c._id === id);
    return company ? company.name : "Unknown";
  };

  // Add or update company-role pair
  const handleSaveCompanyRole = () => {
    if (!currentCompanyId || !currentRole) {
      toast.show("Both company and role must be selected", {
        type: "danger",
      });
      return;
    }

    // Check if this company is already assigned (if we're not editing an existing pair)
    if (editingIndex === -1) {
      const existingPair = companyRolePairs.find(
        (pair) => pair.companyId === currentCompanyId
      );

      if (existingPair) {
        toast.show("This company already has a role assigned", {
          type: "danger",
        });
        return;
      }
    }

    if (editingIndex >= 0) {
      // Update existing pair
      const updatedPairs = [...companyRolePairs];
      updatedPairs[editingIndex] = {
        companyId: currentCompanyId,
        role: currentRole,
      };
      setCompanyRolePairs(updatedPairs);
    } else {
      // Add new pair
      setCompanyRolePairs([
        ...companyRolePairs,
        { companyId: currentCompanyId, role: currentRole },
      ]);
    }

    // Reset and close modal
    setCurrentCompanyId("");
    setCurrentRole("");
    setEditingIndex(-1);
    setEditPairModalVisible(false);
  };

  // Open modal to edit a company-role pair
  const handleEditPair = (index: number) => {
    const pair = companyRolePairs[index];
    setCurrentCompanyId(pair.companyId);
    setCurrentRole(pair.role);
    setEditingIndex(index);
    setEditPairModalVisible(true);
  };

  // Remove a company-role pair
  const handleRemovePair = (index: number) => {
    const updatedPairs = [...companyRolePairs];
    updatedPairs.splice(index, 1);
    setCompanyRolePairs(updatedPairs);
  };

  // Open modal to add a new company-role pair
  const handleAddCompanyRole = () => {
    setCurrentCompanyId("");
    setCurrentRole("");
    setEditingIndex(-1);
    setEditPairModalVisible(true);
  };

  // Form submission handler
  const onSubmit = async (data: FormData) => {
    try {
      // Validate that we have at least one company-role pair
      if (companyRolePairs.length === 0) {
        toast.show("At least one company-role assignment is required", {
          type: "danger",
        });
        return;
      }

      setLoading(true);
      const displayName = `${data.firstName} ${data.lastName}`;

      // Create roles array with company and role information
      const rolesArray = companyRolePairs.map((pair) => ({
        company_id: pair.companyId,
        role: pair.role,
      }));

      const formData = {
        email: data.email,
        zone: data.zone,
        firstName: data.firstName,
        lastName: data.lastName,
        displayName,
        login: data.login,
        team: data.team,
        password: data.password,
        auto_sync: false,
        roles: rolesArray,
        company_ids: companyRolePairs.map((pair) => pair.companyId),
      };

      const result = await createOrganizationalUser(formData);
      setLoading(false);

      if (result.isSuccessfull) {
        toast.show(result?.data?.message, {
          type: "success",
        });
        navigation.reset({
          index: 0,
          routes: [
            {
              name: "AdminHome",
              params: { screen: "AdminOrganizationalUsers" },
            },
          ],
        });
      } else {
        toast.show(result.data, {
          type: "danger",
        });
      }
    } catch (err: any) {
      setLoading(false);
      toast.show(err.message, {
        type: "danger",
      });
    }
  };

  // Form field component to reduce repetition
  const FormField = ({
    name,
    label,
    placeholder,
    secureTextEntry = false,
    rules = {},
  }: {
    name: keyof FormData;
    label: string;
    placeholder: string;
    secureTextEntry?: boolean;
    rules?: object;
  }) => (
    <>
      <Text style={styles.label}>{label}</Text>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInput
            style={[styles.input, errors[name] && styles.inputError]}
            onChangeText={onChange}
            onBlur={onBlur}
            value={value}
            placeholder={placeholder}
            secureTextEntry={secureTextEntry}
          />
        )}
      />
      {errors[name] && (
        <Text style={styles.errorText}>{errors[name]?.message as string}</Text>
      )}
    </>
  );

  // Company-Role Edit Modal
  const CompanyRoleEditModal = () => (
    <Modal
      animationType="slide"
      transparent={true}
      visible={editPairModalVisible}
      onRequestClose={() => setEditPairModalVisible(false)}
    >
      <RNView style={styles.modalContainer}>
        <RNView style={styles.modalContent}>
          <Text style={styles.modalHeader}>
            {editingIndex >= 0 ? "Edit Company Role" : "Add Company Role"}
          </Text>

          {/* Company Selection */}
          <Text style={styles.label}>Company</Text>
          <Picker
            selectedValue={currentCompanyId}
            onValueChange={(value: any) => setCurrentCompanyId(value)}
            style={styles.picker}
            enabled={editingIndex === -1} // Disable company change when editing
          >
            <Picker.Item label="Select a company" value="" />
            {organizations.map((company: any) => (
              <Picker.Item
                key={company._id}
                label={company.name}
                value={company._id}
              />
            ))}
          </Picker>

          {/* Role Selection */}
          <Text style={styles.label}>Role</Text>
          <Picker
            selectedValue={currentRole}
            onValueChange={(value: any) => setCurrentRole(value)}
            style={styles.picker}
          >
            <Picker.Item label="Select a role" value="" />
            {ROLES.map((role) => (
              <Picker.Item
                key={role.value}
                label={role.label}
                value={role.value}
              />
            ))}
          </Picker>

          {/* Action Buttons */}
          <RNView style={styles.modalButtonsContainer}>
            <TouchableOpacity
              style={[
                styles.modalButton,
                { backgroundColor: "#ccc", flex: 1, marginRight: 8 },
              ]}
              onPress={() => setEditPairModalVisible(false)}
            >
              <Text style={styles.modalButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalButton, { flex: 1, marginLeft: 8 }]}
              onPress={handleSaveCompanyRole}
            >
              <Text style={styles.modalButtonText}>Save</Text>
            </TouchableOpacity>
          </RNView>
        </RNView>
      </RNView>
    </Modal>
  );

  return (
    <ScrollView
      style={{
        height: "100%",
        backgroundColor: colorScheme === "light" ? "#fafafa" : "#fafafa",
      }}
    >
      {/* Form container */}
      <View style={styles.container}>
        <View style={styles.box}>
          <View style={{ paddingHorizontal: 15, paddingVertical: 5 }}>
            <TouchableOpacity
              onPress={() => navigation.navigate("AdminOrganizationalUsers")}
            >
              <AntDesign name="arrowleft" size={24} color="black" />
            </TouchableOpacity>
          </View>
          <Text style={styles.header}>Create User Account Form</Text>

          {/* Email Field */}
          <FormField
            name="email"
            label="Email"
            placeholder="Enter email"
            rules={{
              required: "Email is required",
              pattern: {
                value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                message: "Email is not valid",
              },
            }}
          />

          {/* Zone Field */}
          <Text style={styles.label}>Zone</Text>
          <Controller
            control={control}
            name="zone"
            rules={{ required: "Zone is required" }}
            render={({ field: { onChange, value } }) => (
              <Picker
                selectedValue={value}
                onValueChange={onChange}
                style={styles.picker}
              >
                <Picker.Item label="Select a zone" value="" />
                {TIMEZONES.map((zone) => (
                  <Picker.Item
                    key={zone.value}
                    label={zone.label}
                    value={zone.value}
                  />
                ))}
              </Picker>
            )}
          />
          {errors.zone && (
            <Text style={styles.errorText}>{errors.zone.message}</Text>
          )}

          {/* First Name Field */}
          <FormField
            name="firstName"
            label="First Name"
            placeholder="Enter first name"
            rules={{ required: "First name is required" }}
          />

          {/* Last Name Field */}
          <FormField
            name="lastName"
            label="Last Name"
            placeholder="Enter last name"
            rules={{ required: "Last name is required" }}
          />

          {/* Username Field */}
          <FormField
            name="login"
            label="Username"
            placeholder="Enter username"
            rules={{
              required: "Username is required",
              maxLength: {
                value: 11,
                message: "Username must not exceed 11 characters",
              },
            }}
          />

          {/* Team Field */}
          <Text style={styles.label}>Team</Text>
          <Controller
            control={control}
            name="team"
            rules={{ required: "Team is required" }}
            render={({ field: { onChange, value } }) => (
              <Picker
                selectedValue={value}
                onValueChange={onChange}
                style={styles.picker}
              >
                <Picker.Item label="Select a team" value="" />
                {TEAMS.map((team) => (
                  <Picker.Item
                    key={team.value}
                    label={team.label}
                    value={team.value}
                  />
                ))}
              </Picker>
            )}
          />
          {errors.team && (
            <Text style={styles.errorText}>{errors.team.message}</Text>
          )}

          {/* Company-Role Assignments Section */}
          <View style={styles.sectionContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Company-Role Assignments</Text>
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddCompanyRole}
              >
                <AntDesign name="plus" size={16} color="white" />
                <Text style={styles.addButtonText}>Add</Text>
              </TouchableOpacity>
            </View>

            {companyRolePairs.length === 0 ? (
              <Text style={styles.noPairsText}>
                No company-role assignments. Add at least one.
              </Text>
            ) : (
              <View style={styles.pairsContainer}>
                {companyRolePairs.map((pair, index) => (
                  <View key={index} style={styles.pairItem}>
                    <View style={styles.pairInfo}>
                      <Text style={styles.companyName}>
                        {getCompanyNameById(pair.companyId)}
                      </Text>
                      <Text style={styles.roleBadge}>{pair.role}</Text>
                    </View>
                    <View style={styles.pairActions}>
                      <TouchableOpacity
                        style={styles.actionButton}
                        onPress={() => handleEditPair(index)}
                      >
                        <AntDesign name="edit" size={18} color="#1E90FF" />
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.actionButton}
                        onPress={() => handleRemovePair(index)}
                      >
                        <AntDesign name="delete" size={18} color="#FF4500" />
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Password Field */}
          <FormField
            name="password"
            label="Password"
            placeholder="Enter password"
            secureTextEntry
            rules={{
              required: "Password is required",
              maxLength: {
                value: 11,
                message: "Password must not exceed 11 characters",
              },
            }}
          />

          {/* Submit Button */}
          <TouchableOpacity
            style={styles.button}
            onPress={handleSubmit(onSubmit)}
            disabled={loading}
          >
            <Text style={styles.buttonText}>
              {loading ? "Submitting..." : "Submit"}
            </Text>
          </TouchableOpacity>

          {/* Render Modal */}
          <CompanyRoleEditModal />
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: width * 0.05,
    paddingVertical: height * 0.02,
    backgroundColor: "#f8f9fa",
    width: "100%",
    marginTop: 50,
    marginBottom: 50,
  },
  box: {
    width: "100%",
    maxWidth: 800,
    padding: 16,
    borderRadius: 12,
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 20,
    textAlign: "center",
    color: "#333",
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8,
    color: "#555",
  },
  input: {
    width: "100%",
    height: 50,
    backgroundColor: "#fff",
    paddingHorizontal: 16,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 16,
    color: "#333",
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  inputError: {
    borderColor: "red",
    backgroundColor: "#ffe6e6",
  },
  errorText: {
    color: "red",
    fontSize: 14,
    marginBottom: 8,
    textAlign: "left",
  },
  button: {
    alignItems: "center",
    justifyContent: "center",
    height: 50,
    backgroundColor: "#1E90FF",
    borderRadius: 8,
    marginTop: 20,
    width: "100%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 18,
  },
  picker: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    fontSize: 15,
    marginTop: 10,
    marginBottom: 10,
  },
  modalContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  modalContent: {
    width: "80%",
    backgroundColor: "white",
    borderRadius: 10,
    padding: 20,
  },
  modalHeader: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 15,
    textAlign: "center",
  },
  modalButton: {
    backgroundColor: "#1E90FF",
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: "center",
  },
  modalButtonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  modalButtonsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 20,
  },
  sectionContainer: {
    marginVertical: 16,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 8,
    padding: 12,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#4CAF50",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  addButtonText: {
    color: "white",
    fontWeight: "bold",
    marginLeft: 4,
  },
  pairsContainer: {
    marginTop: 8,
  },
  pairItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  pairInfo: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  companyName: {
    fontSize: 16,
    color: "#333",
    flex: 1,
  },
  roleBadge: {
    backgroundColor: "#E3F2FD",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    color: "#1565C0",
    fontWeight: "600",
    fontSize: 14,
  },
  pairActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  actionButton: {
    padding: 8,
    marginLeft: 8,
  },
  noPairsText: {
    color: "#888",
    fontStyle: "italic",
    textAlign: "center",
    paddingVertical: 16,
  },
});
